import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "react-hot-toast";

const GROUP_LABELS = {
  procurement: "Procurement",
  vendor: "Vendors",
  inventory: "Inventory",
  invoice: "Invoices",
  event: "Events",
  maintenance: "Maintenance",
  report: "Reports",
};

const ACTION_LABELS = {
  view: "View",
  create: "Create",
  edit: "Edit",
  dispatch: "Dispatch",
  receive: "Receive",
  reject: "Reject",
  backorder: "Backorder",
  manage: "Manage",
  adjust: "Adjust",
  transfer: "Transfer",
  upload: "Upload",
  approve: "Approve",
  pay: "Pay",
};

function titleCase(value) {
  return String(value || "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function apiError(err, fallback = "Request failed") {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

function currentAppointment(row) {
  if (!row || row.is_active !== true || row.revoked_at) return false;
  const now = Date.now();
  if (row.valid_from && new Date(row.valid_from).getTime() > now) return false;
  if (row.valid_until && new Date(row.valid_until).getTime() < now) return false;
  return true;
}

function moneyLimit(value) {
  if (value == null || value === "") return "Unlimited";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 2,
  }).format(n);
}

function controlKey(row) {
  return [row?.module, row?.component, row?.access_level]
    .map((v) => String(v || "").trim().toLowerCase())
    .join(".");
}

function parseCatalogKey(key) {
  const [module, component, access_level] = String(key || "")
    .trim()
    .toLowerCase()
    .split(".");
  return { module, component, access_level };
}

export default function AccessControlPanel() {
  const [companyId, setCompanyId] = useState("");
  const [companyData, setCompanyData] = useState(null);
  const [managerId, setManagerId] = useState("");
  const [permissionData, setPermissionData] = useState(null);
  const [permissionStates, setPermissionStates] = useState({});
  const permissionStatesRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approvalDrafts, setApprovalDrafts] = useState({});
  const [approvalReason, setApprovalReason] = useState("");

  function replacePermissionStates(next) {
    permissionStatesRef.current = next;
    setPermissionStates(next);
  }

  function setPermissionState(key, value) {
    const normalizedKey = String(key || "").trim().toLowerCase();
    permissionStatesRef.current = {
      ...permissionStatesRef.current,
      [normalizedKey]: value,
    };
    setPermissionStates(permissionStatesRef.current);
  }

  const managers = useMemo(() => {
    return (companyData?.users || []).filter(
      (u) => String(u?.role || "").toLowerCase() === "manager"
    );
  }, [companyData]);

  const selectedManager = useMemo(() => {
    return managers.find((u) => String(u.user_id) === String(managerId)) || null;
  }, [managers, managerId]);

  const assignedCostCentreIds = useMemo(() => {
    return new Set(
      (selectedManager?.cost_centre_ids || [])
        .map(Number)
        .filter(Number.isFinite)
    );
  }, [selectedManager]);

  const assignedCostCentres = useMemo(() => {
    return (companyData?.cost_centres || []).filter((cc) =>
      assignedCostCentreIds.has(Number(cc.cost_centre_id))
    );
  }, [companyData, assignedCostCentreIds]);

  const activeAppointments = useMemo(() => {
    if (!selectedManager?.staff_id) return [];
    return (companyData?.appointments || []).filter(
      (a) =>
        Number(a.staff_id) === Number(selectedManager.staff_id) &&
        String(a.module || "").toLowerCase() === "procurement" &&
        String(a.component || "").toLowerCase() === "po" &&
        String(a.access_level || "").toLowerCase() === "approve" &&
        a.is_allowed === true &&
        currentAppointment(a)
    );
  }, [companyData, selectedManager]);

  const activeAppointmentByCc = useMemo(() => {
    const map = new Map();
    activeAppointments.forEach((a) => {
      map.set(Number(a.cost_centre_id), a);
    });
    return map;
  }, [activeAppointments]);

  const groupedCatalog = useMemo(() => {
    const groups = {};
    for (const rawKey of permissionData?.managed_permission_catalog || []) {
      const key = String(rawKey || "").trim().toLowerCase();
      const parsed = parseCatalogKey(key);
      if (!groups[parsed.module]) groups[parsed.module] = [];
      groups[parsed.module].push({ key, ...parsed });
    }
    return groups;
  }, [permissionData]);

  const scopedControls = useMemo(() => {
    return (permissionData?.permissions || []).filter(
      (row) => row.unit_id != null || row.cost_centre_id != null
    );
  }, [permissionData]);

  function statesFromPermissions(rows) {
    const states = {};
    for (const row of rows || []) {
      if (row.unit_id == null && row.cost_centre_id == null) {
        states[controlKey(row)] = row.is_allowed === true ? "allow" : "deny";
      }
    }
    return states;
  }

  async function loadCompany() {
    const id = String(companyId || "").trim();
    if (!/^[1-9]\d*$/.test(id)) {
      toast.error("Enter a valid company ID.");
      return;
    }

    setLoading(true);
    setManagerId("");
    setPermissionData(null);
    replacePermissionStates({});
    try {
      const { data } = await api.get(`/admin/approval-settings/companies/${id}`);
      setCompanyData(data);
      toast.success(`Loaded ${data?.company?.company_name || `Company #${id}`}`);
    } catch (err) {
      setCompanyData(null);
      toast.error(apiError(err, "Failed to load company governance data"));
    } finally {
      setLoading(false);
    }
  }

  async function loadManager(nextManagerId) {
    const id = String(nextManagerId || "");
    setManagerId(id);
    setPermissionData(null);
    replacePermissionStates({});
    setApprovalDrafts({});
    setApprovalReason("");
    if (!id) return;

    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/companies/${companyId}/users/${id}/permissions`
      );
      setPermissionData(data);
      replacePermissionStates(statesFromPermissions(data?.permissions));
    } catch (err) {
      toast.error(apiError(err, "Failed to load Manager permissions"));
    } finally {
      setLoading(false);
    }
  }

  async function refreshSelectedManager() {
    const id = managerId;
    if (!id) return;
    setLoading(true);
    try {
      const [approvalRes, permissionRes] = await Promise.all([
        api.get(`/admin/approval-settings/companies/${companyId}`),
        api.get(`/admin/companies/${companyId}/users/${id}/permissions`),
      ]);
      setCompanyData(approvalRes.data);
      setPermissionData(permissionRes.data);
      replacePermissionStates(statesFromPermissions(permissionRes.data?.permissions));
    } catch (err) {
      toast.error(apiError(err, "Refresh failed"));
    } finally {
      setLoading(false);
    }
  }

  async function saveOperationalPermissions() {
    if (!managerId || !permissionData) return;
    setSaving(true);
    const t = toast.loading("Saving Manager permissions...");
    try {
      const payload = [];

      for (const row of scopedControls) {
        payload.push({
          module: row.module,
          component: row.component,
          access_level: row.access_level,
          is_allowed: row.is_allowed === true,
          unit_id: row.unit_id ?? null,
          cost_centre_id: row.cost_centre_id ?? null,
        });
      }

      const latestStates = permissionStatesRef.current;
      for (const rawKey of permissionData.managed_permission_catalog || []) {
        const key = String(rawKey || "").trim().toLowerCase();
        const state = latestStates[key] || "inherit";
        if (state === "inherit") continue;
        const parsed = parseCatalogKey(key);
        payload.push({
          ...parsed,
          is_allowed: state === "allow",
          unit_id: null,
          cost_centre_id: null,
        });
      }

      await api.put(
        `/admin/companies/${companyId}/users/${managerId}/permissions`,
        { permissions: payload }
      );
      toast.success("Operational permissions saved", { id: t });
      await refreshSelectedManager();
    } catch (err) {
      toast.error(apiError(err, "Failed to save permissions"), { id: t });
    } finally {
      setSaving(false);
    }
  }

  function updateApprovalDraft(costCentreId, patch) {
    const key = String(costCentreId);
    setApprovalDrafts((prev) => ({
      ...prev,
      [key]: {
        mode: "finite",
        limit: "",
        ...(prev[key] || {}),
        ...patch,
      },
    }));
  }

  async function grantApproval(costCentre) {
    if (!selectedManager) return;
    const reason = approvalReason.trim();
    if (!reason) {
      toast.error("Enter a governance reason before changing approval authority.");
      return;
    }

    const draft = approvalDrafts[String(costCentre.cost_centre_id)] || {
      mode: "finite",
      limit: "",
    };

    if (draft.mode === "finite" && !/^(0|[1-9]\d{0,11})(\.\d{1,2})?$/.test(String(draft.limit))) {
      toast.error("Enter a valid approval limit with up to two decimal places.");
      return;
    }

    const unitId = Number(selectedManager.staff_unit_id || selectedManager.unit_id);
    if (!Number.isFinite(unitId) || unitId <= 0) {
      toast.error("This Manager does not have a valid staff unit.");
      return;
    }

    setSaving(true);
    const t = toast.loading("Granting PO approval authority...");
    try {
      await api.post(`/admin/approval-settings/companies/${companyId}/appointments`, {
        user_id: Number(selectedManager.user_id),
        unit_id: unitId,
        cost_centre_id: Number(costCentre.cost_centre_id),
        unlimited: draft.mode === "unlimited",
        approval_limit: draft.mode === "unlimited" ? null : String(draft.limit),
        valid_from: null,
        valid_until: null,
        reason,
      });
      toast.success("PO approval authority granted", { id: t });
      setApprovalReason("");
      await refreshSelectedManager();
    } catch (err) {
      toast.error(apiError(err, "Failed to grant approval authority"), { id: t });
    } finally {
      setSaving(false);
    }
  }

  async function revokeApproval(appointment) {
    const reason = approvalReason.trim();
    if (!reason) {
      toast.error("Enter a governance reason before revoking approval authority.");
      return;
    }

    setSaving(true);
    const t = toast.loading("Revoking PO approval authority...");
    try {
      await api.post(
        `/admin/approval-settings/companies/${companyId}/appointments/${appointment.id}/revoke`,
        { reason }
      );
      toast.success("PO approval authority revoked", { id: t });
      setApprovalReason("");
      await refreshSelectedManager();
    } catch (err) {
      toast.error(apiError(err, "Failed to revoke approval authority"), { id: t });
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="mx-auto max-w-7xl space-y-6 p-4 text-slate-900 md:p-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700">
            Platform Governance
          </div>
          <h1 className="mt-1 text-3xl font-bold">Manager Permissions & PO Authority</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Configure operational access independently from financial PO approval authority.
            Backend authorization remains authoritative.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/admin">Back to Admin</Link>
        </Button>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[220px_auto] md:items-end">
          <label className="text-sm font-medium">
            Company ID
            <Input
              className="mt-1"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              placeholder="e.g. 12"
              inputMode="numeric"
            />
          </label>
          <div className="flex gap-2">
            <Button onClick={loadCompany} disabled={loading || saving} type="button">
              {loading ? "Loading..." : "Load Company"}
            </Button>
            {companyData?.company?.id ? (
              <Button variant="outline" asChild>
                <Link to={`/admin/companies/${companyData.company.id}`}>
                  Open Company Setup
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
      </section>

      {companyData ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold">
                {companyData.company?.company_name || `Company #${companyId}`}
              </h2>
              <p className="text-sm text-slate-500">Choose a Manager to configure.</p>
            </div>
            <Button variant="outline" onClick={refreshSelectedManager} disabled={!managerId || loading || saving}>
              Refresh
            </Button>
          </div>

          <label className="mt-4 block max-w-xl text-sm font-medium">
            Manager
            <select
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
              value={managerId}
              onChange={(e) => loadManager(e.target.value)}
            >
              <option value="">Choose a Manager</option>
              {managers.map((m) => (
                <option key={m.user_id} value={m.user_id} disabled={!m.is_active}>
                  {m.full_name} · Staff #{m.staff_id || "unlinked"} {m.is_active ? "" : "· inactive"}
                </option>
              ))}
            </select>
          </label>
        </section>
      ) : null}

      {selectedManager && permissionData ? (
        <>
          <section className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Manager</div>
              <div className="mt-1 text-lg font-semibold">{selectedManager.full_name}</div>
              <div className="text-sm text-slate-500">Staff #{selectedManager.staff_id}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Assigned Cost Centres</div>
              <div className="mt-1 text-lg font-semibold">{assignedCostCentres.length}</div>
              <div className="text-sm text-slate-500">
                {assignedCostCentres.map((cc) => cc.code || cc.cost_centre_id).join(", ") || "None"}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">PO Approval</div>
              <div className={`mt-1 text-lg font-semibold ${activeAppointments.length ? "text-emerald-700" : "text-slate-700"}`}>
                {activeAppointments.length ? "Allowed" : "Not appointed"}
              </div>
              <div className="text-sm text-slate-500">
                {activeAppointments.length} active cost-centre appointment(s)
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold">Operational Permissions</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Inherit uses the Manager base role. Allow and Deny create explicit personal overrides.
                </p>
              </div>
              <Button onClick={saveOperationalPermissions} disabled={saving || loading}>
                {saving ? "Saving..." : "Save Operational Permissions"}
              </Button>
            </div>

            <div className="mt-5 space-y-5">
              {Object.entries(groupedCatalog).map(([moduleName, rows]) => (
                <div key={moduleName} className="overflow-hidden rounded-xl border border-slate-200">
                  <div className="bg-slate-900 px-4 py-3 font-semibold text-white">
                    {GROUP_LABELS[moduleName] || titleCase(moduleName)}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {rows.map((row) => {
                      const state = permissionStates[row.key] || "inherit";
                      return (
                        <div key={row.key} className="grid gap-3 p-3 md:grid-cols-[1fr_220px] md:items-center">
                          <div>
                            <div className="font-medium">
                              {titleCase(row.component)} · {ACTION_LABELS[row.access_level] || titleCase(row.access_level)}
                            </div>
                            <div className="text-xs text-slate-400">{row.key}</div>
                          </div>
                          <select
                            className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                              state === "allow"
                                ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                                : state === "deny"
                                  ? "border-rose-300 bg-rose-50 text-rose-800"
                                  : "border-slate-300 bg-slate-50 text-slate-700"
                            }`}
                            value={state}
                            onChange={(e) => setPermissionState(row.key, e.target.value)}
                          >
                            <option value="inherit">Inherit base role</option>
                            <option value="allow">Allow</option>
                            <option value="deny">Deny</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div>
              <h2 className="text-xl font-semibold">PO Approval Authority</h2>
              <p className="mt-1 text-sm text-slate-500">
                Approval is opt-in. Each cost centre has its own independent financial limit.
                Removing an appointment removes approval authority for that cost centre.
              </p>
            </div>

            <label className="mt-4 block max-w-2xl text-sm font-medium">
              Governance reason
              <Input
                className="mt-1"
                value={approvalReason}
                onChange={(e) => setApprovalReason(e.target.value)}
                placeholder="Required for every grant or revoke"
              />
            </label>

            <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[820px] text-sm">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-3 text-left">Cost Centre</th>
                    <th className="p-3 text-left">Current Authority</th>
                    <th className="p-3 text-left">New Limit</th>
                    <th className="p-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {assignedCostCentres.map((cc) => {
                    const appointment = activeAppointmentByCc.get(Number(cc.cost_centre_id));
                    const draft = approvalDrafts[String(cc.cost_centre_id)] || {
                      mode: "finite",
                      limit: "",
                    };
                    return (
                      <tr key={cc.cost_centre_id} className="align-top">
                        <td className="p-3">
                          <div className="font-semibold">{cc.code || `CC${cc.cost_centre_id}`}</div>
                          <div className="text-xs text-slate-500">{cc.name || cc.cost_centre_name || `Cost centre #${cc.cost_centre_id}`}</div>
                        </td>
                        <td className="p-3">
                          {appointment ? (
                            <div>
                              <span className="inline-flex rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                                Approval allowed
                              </span>
                              <div className="mt-2 font-semibold">{moneyLimit(appointment.approval_limit)}</div>
                            </div>
                          ) : (
                            <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                              No approval authority
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          {!appointment ? (
                            <div className="space-y-2">
                              <select
                                className="w-full rounded-lg border border-slate-300 px-3 py-2"
                                value={draft.mode}
                                onChange={(e) =>
                                  updateApprovalDraft(cc.cost_centre_id, { mode: e.target.value })
                                }
                              >
                                <option value="finite">Set financial limit</option>
                                <option value="unlimited">Unlimited</option>
                              </select>
                              {draft.mode === "finite" ? (
                                <Input
                                  value={draft.limit}
                                  onChange={(e) =>
                                    updateApprovalDraft(cc.cost_centre_id, { limit: e.target.value })
                                  }
                                  placeholder="e.g. 10000.00"
                                  inputMode="decimal"
                                />
                              ) : null}
                            </div>
                          ) : (
                            <div className="text-xs text-slate-500">
                              Revoke the current appointment before assigning a different limit.
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          {appointment ? (
                            <Button
                              variant="outline"
                              disabled={saving}
                              onClick={() => revokeApproval(appointment)}
                            >
                              Revoke
                            </Button>
                          ) : (
                            <Button
                              disabled={saving}
                              onClick={() => grantApproval(cc)}
                            >
                              Allow Approval
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {assignedCostCentres.length === 0 ? (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                This Manager has no assigned cost centres. Assign cost centres from Company Users first.
              </div>
            ) : null}
          </section>
        </>
      ) : null}
    </main>
  );
}